import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import compose from 'lodash.flowright';
import { graphql } from 'react-apollo';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import pMap from 'p-map-browser';
import useClipboard from 'react-use-clipboard';
import qs from 'query-string';

import Spinner from '../../../../../../components/global/Spinner';
import {
  AlbumForContributorShare,
  AlbumFolderForContributorShare,
} from '../../../../../../graphql/queries/contributors';
import { autoContributorInvitationsSend as autoContributorInvitationsSendMutation } from '../../../../../../graphql/mutations/contributor-assignments';
import { requestConfirm as requestConfirmAction } from '../../../../../../actions/modal-actions';
import AccountSetupModal from '../../../../hoc/RequireShutterbugAccountSetup/components/AccountSetupModal';
import useShutterbugAccountComplete from '../../../../hoc/RequireShutterbugAccountSetup/hooks/useShutterbugAccountComplete';
import Button from '../../../../../../components/global/Button';
import { isAlbumFolder } from '../../../../../../util/album-folder';
import {
  isAlbumIndividual,
  isMemoryBook,
  isVGBContext,
  isVideoAlbum,
  isWeddingsContent,
} from '../../../../../../util/album';
import Angle from '../../../../../../components/global/Angle';
import LinkIcon from '../../../../../../components/icons/LinkIcon';
import QRIcon from '../../../../../../components/icons/QRIcon';
import {
  isAlbumFolderOwner,
  isAlbumOwner,
} from '../../../../../../util/account';
import useFeaturePermission, {
  FEATURE_PERMISSION_TYPE,
} from '../../../../services/featurePermissions';
import WeddingEventFeatureUpsell from '../../../../views/AlbumFolder/WeddingEvents/WeddingEventFeatureUpsell';
import useAlbumFolderMarketingAssets from '../../../../views/AlbumFolder/AlbumFolderMarketing/components/AlbumFolderMarketingList/useAlbumFolderMarketingAssets';
import { MARKETING_MATERIAL_TYPE } from '../../../../views/AlbumFolder/AlbumFolderMarketing/constants';
import AssetDownloadButton from '../../../../views/AlbumFolder/AlbumFolderMarketing/components/AssetDownloadButton';
import ContextualString from '../../../../../../components/contextual/ContextualString';
import SearchSubscriptionFeatureUpsell from '../../../../../temp/views/onboarding/PhotoSearchUpsell/components/SearchSubscriptionFeatureUpsell';
import {
  PURCHASED_EVENT_ADDON_FEATURES,
  PURCHASED_EVENT_FEATURES,
  SEARCH_SUBSCRIPTION_FEATURES,
} from '../../../../services/featurePermissions/helpers';
import WaldoficationFeatureUpsellBanner from '../../../../../../components/global/WaldoficationFeatureUpsell/components/WaldoficationFeatureUpsellBanner';
import { EVENT_ADDON_ITEM_TYPE } from '../../../../../../constants/event-items';
import MemoryBookGreetingVideo from './components/MemoryBookGreetingVideo';
import { AlbumWithGreetingVideo } from '../../../../../../graphql/queries/albums';
import { VideoDelete as VideoDeleteMutation } from '../../../../../../graphql/mutations/video';
import useOpenAlert from '../../../../../../hooks/useOpenAlert';
import useBasename from '../../../../../../components/routing/useBasename';
import {
  ALBUM_SLIDESHOW_STATUS,
  ALBUM_SUBTYPE,
  ALBUM_TYPE,
} from '../../../../../../constants/albums';
import { getErrorString } from '../../../../../../util/normalize-errors';
import { BASENAME } from '../../../../../../constants/basenames';
import EmailStep from './components/EmailStep';
import PhoneStep from './components/PhoneStep';
import EmailIcon from '../../../../../../components/icons/EmailIcon';
import MarketingMaterialsShare from '../MarketingMaterialsShare/MarketingMaterialsShare';
import HelpBubblesIcon from '../../../../../../components/icons/HelpBubblesIcon';
import DismissButton from '../../../../../../components/global/DismissButton';
import useGlobalToast from '../../../../../../hooks/useGlobalToast';
import SaveTheDateSetup from './components/SaveTheDateSetup/SaveTheDateSetup';

const propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func,
  }),
  albumFolder: PropTypes.shape({
    id: PropTypes.string,
  }),
  album: PropTypes.shape({
    id: PropTypes.string,
    auto_contributor_share_url: PropTypes.string,
    album_folder: PropTypes.shape({
      id: PropTypes.string,
      auto_contributor_share_url: PropTypes.string,
    }),
  }),
  greetingVideo: PropTypes.shape({
    id: PropTypes.string,
  }),
  isDefaultGreetingVideo: PropTypes.bool,
  videoDelete: PropTypes.func,
  onRequestClose: PropTypes.func.isRequired,
  autoContributorInvitationsSend: PropTypes.func.isRequired,
  className: PropTypes.string,
  headerless: PropTypes.bool,
  withSaveTheDateSetup: PropTypes.bool,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
};

const STEPS = {
  SHARE: 'SHARE',
  MARKETING: 'MARKETING',
  QR: 'QR',
};

const SUB_STEPS = {
  SMS: 'SMS',
  EMAIL: 'EMAIL',
};

const ShutterbugShare = ({
  history,
  album,
  albumFolder,
  greetingVideo,
  isDefaultGreetingVideo,
  videoDelete,
  onRequestClose,
  autoContributorInvitationsSend,
  className,
  headerless = false,
  withSaveTheDateSetup = true,
  title: titleProp,
}) => {
  const [step, setStep] = useState(STEPS.SHARE);
  const [subStep, setSubStep] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { setToast } = useGlobalToast();
  const [copiedEmbed, setCopiedEmbed] = useClipboard(
    album?.slideshow?.embed_code,
  );
  const openAlert = useOpenAlert();
  const basename = useBasename();
  const isSpecialtyAlbum =
    album?.is_memory_book ||
    album?.purchased_event_addon_types?.find(type =>
      [
        EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK,
        EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO,
      ].includes(type),
    );
  const isSlideshowPublished =
    album?.slideshow?.status === ALBUM_SLIDESHOW_STATUS.PUBLISHED;

  const onVideoDelete = async video => {
    if (deleting) {
      return;
    }

    setDeleting(true);
    try {
      await videoDelete({ video_id: video.id });
    } catch (err) {
      openAlert({
        message: getErrorString(err),
      });
    } finally {
      setDeleting(false);
    }
  };

  const entity =
    isAlbumIndividual(album) ||
    album?.is_video_album ||
    album?.slideshow?.status === ALBUM_SLIDESHOW_STATUS.PUBLISHED ||
    (album?.album_type === ALBUM_TYPE.EVENT &&
      album?.album_subtype !== ALBUM_SUBTYPE.WEDDINGS)
      ? album
      : (albumFolder ?? album);

  const appendAlbumAsSource = url => {
    return `${url}${
      album
        ? `?${qs.stringify({
            source: album?.id,
          })}`
        : ''
    }`;
  };

  const [copied, setCopied] = useClipboard(
    appendAlbumAsSource(entity?.auto_contributor_share_url),
  );
  const { marketingMaterials } = useAlbumFolderMarketingAssets({
    albumFolderId: albumFolder?.id,
  });

  const QRCodeMarketingMaterial = marketingMaterials?.find(
    marketingMaterial =>
      marketingMaterial.type === MARKETING_MATERIAL_TYPE.QR_CODE_SHARE,
  );

  const featurePermissionItem = useFeaturePermission({
    albumFolderId: albumFolder?.id,
    features: [
      SEARCH_SUBSCRIPTION_FEATURES.white_label_marketing_prints_enabled,
      SEARCH_SUBSCRIPTION_FEATURES.qr_code_enabled,
      SEARCH_SUBSCRIPTION_FEATURES.marketing_prints_enabled,
    ],
  });

  const {
    loading: loadingAccountSetup,
    isAccountSetupComplete,
    user,
  } = useShutterbugAccountComplete();
  const [accountSetupFormOpen, setAccountSetupFormOpen] = useState(false);

  useEffect(() => {
    if (loadingAccountSetup) {
      return;
    }

    if (!isAccountSetupComplete && user) {
      setAccountSetupFormOpen(true);
    } else {
      setAccountSetupFormOpen(false);
    }
  }, [loadingAccountSetup, isAccountSetupComplete]);

  useEffect(() => {
    if (success) {
      setToast('Your invites have been sent!');
      onRequestClose?.();
    }
  }, [success]);

  const isOwner =
    entity === album
      ? isAlbumOwner(user, album)
      : isAlbumFolderOwner(user, albumFolder);

  const isWeddingsBool = isWeddingsContent(entity);
  const isVGB = isVideoAlbum(album) && isVGBContext(album);

  const showMarketingMaterials = (() => {
    if (step === STEPS.QR) {
      return false;
    }

    if (!isOwner) {
      return false;
    }

    if (isMemoryBook(album)) {
      return false;
    }

    return basename !== BASENAME.SHUTTERBUG || isVGB;
  })();

  useEffect(() => {
    if (
      isWeddingsBool &&
      showMarketingMaterials &&
      featurePermissionItem?.marketing_prints_enabled?.enabled
    ) {
      setStep(STEPS.MARKETING);
    }
  }, [featurePermissionItem?.marketing_prints_enabled?.enabled]);

  const onSendInvites = async (
    invitees,
    input,
    inputIsPhone,
    circleInvitees,
  ) => {
    setSuccess(false);
    setLoading(true);

    try {
      if (invitees.length > 0 || input) {
        const emailAddresses = [
          ...invitees.map(invitee => invitee.email),
          !inputIsPhone ? input : undefined,
        ].filter(Boolean);
        const phoneNumbers = [
          ...invitees.map(invitee => invitee.phoneNumber),
          inputIsPhone ? input : undefined,
        ].filter(Boolean);

        await autoContributorInvitationsSend({
          album_id: !isAlbumFolder(entity) ? entity?.id : undefined,
          album_folder_id: isAlbumFolder(entity) ? entity?.id : undefined,
          email_addresses: emailAddresses,
          phone_numbers: phoneNumbers,
        });
      }

      await pMap(
        circleInvitees,
        async circleId => {
          await autoContributorInvitationsSend({
            album_id: !isAlbumFolder(entity) ? entity?.id : undefined,
            album_folder_id: isAlbumFolder(entity) ? entity?.id : undefined,
            circle_id: circleId,
          });
        },
        { concurrency: 3 },
      );

      setSuccess(true);
      setLoading(false);
      return [];
    } catch (err) {
      setLoading(false);
      return [getErrorString(err)];
    }
  };

  if (!album && !albumFolder) {
    return (
      <div className="d-flex">
        <div className="p-5 m-auto">
          <Spinner />
        </div>
      </div>
    );
  }

  const title = (() => {
    if (titleProp) {
      return titleProp;
    }
    if (isVGB) {
      return (
        <Fragment>
          Share <span className="text-primary">Video Guestbook</span> with
          others
        </Fragment>
      );
    }
    if (isVideoAlbum(album)) {
      return <Fragment>Group Video</Fragment>;
    }

    if (isMemoryBook(album)) {
      return <Fragment>Memories Slideshow</Fragment>;
    }

    return (
      <Fragment>
        <ContextualString
          id="inviteModalHeader"
          defaultMessage="Invite to"
          type={entity?.album_type}
          subtype={entity?.album_subtype}
        />{' '}
        <span className="text-primary">{entity?.name}</span>
      </Fragment>
    );
  })();

  const greetingVideoTitle = (() => {
    if (isVGB) {
      return 'Invite others to your Video Guestbook';
    }

    if (isVideoAlbum(album)) {
      return 'Request videos from others prompt';
    }

    return 'Request photos from others prompt';
  })();

  const greetingVideoSubtitle = (() => {
    if (!isVGB && isVideoAlbum(album)) {
      return 'Those invited to add videos will see this prompt:';
    }

    return 'Those invited will see the greeting video below:';
  })();

  const shareTitle = (() => {
    if (isVGB) {
      return 'Invite to Video Guestbook via';
    }

    if (isVideoAlbum(album)) {
      return 'Request videos from others';
    }

    if (isMemoryBook(album) && isSlideshowPublished) {
      return 'Invite others to the slideshow';
    }

    if (isMemoryBook(album)) {
      return 'Request old photos from others';
    }

    return 'Invite others to the event';
  })();

  const classes = classNames({
    [className]: !!className,
    'px-lg-2 pb-3': true,
  });

  return (
    <Fragment>
      <div className={classes}>
        {step !== STEPS.QR && !headerless && (
          <div className="d-flex flex-column align-items-center mb-0">
            <div className="w-100 d-flex align-items-center mb-1">
              <h3
                className={classNames({
                  'text-center w-100 mb-0': true,
                })}
              >
                {title}
              </h3>
              {onRequestClose && (
                <DismissButton
                  className="contributor-dismiss-btn"
                  onClick={onRequestClose}
                />
              )}
            </div>
          </div>
        )}
        {showMarketingMaterials && (
          <div
            className={
              isWeddingsBool
                ? 'shutterbug-share-section-selector flipped'
                : 'shutterbug-share-section-selector'
            }
          >
            <Button
              btnStyle="reset"
              className={classNames({
                'shutterbug-share-section-button': true,
                'is-active': step === STEPS.SHARE,
              })}
              onClick={() => setStep(STEPS.SHARE)}
            >
              Share a link
            </Button>
            {featurePermissionItem.marketing_prints_enabled.enabled && (
              <Button
                btnStyle="reset"
                className={classNames({
                  'shutterbug-share-section-button': true,
                  'is-active': step === STEPS.MARKETING,
                })}
                onClick={() => setStep(STEPS.MARKETING)}
              >
                Print QR signage
              </Button>
            )}
          </div>
        )}
        {step === STEPS.SHARE && isSpecialtyAlbum && (
          <Fragment>
            {greetingVideo && isOwner && (
              <Fragment>
                <h4 className="mb-1">{greetingVideoTitle}</h4>
                <p className="text-light text-xs mb-1">
                  {greetingVideoSubtitle}
                </p>
              </Fragment>
            )}
            <MemoryBookGreetingVideo
              isOwner={isOwner}
              greetingVideo={greetingVideo}
              isDefaultGreetingVideo={isDefaultGreetingVideo}
              onRecordVideo={() => {
                history.push(
                  `/app/${basename}/albums/${album?.id}/record-video?forceVideo=1`,
                );
                onRequestClose();
              }}
              onDeleteVideo={
                isOwner
                  ? () => {
                      onVideoDelete(greetingVideo);
                    }
                  : undefined
              }
            />
            {isOwner && !!greetingVideo && isDefaultGreetingVideo && (
              <Button
                btnBlock
                btnStyle="link"
                className="record-greeting-video-cta"
                onClick={() => {
                  history.push(
                    `/app/${basename}/albums/${album?.id}/record-video?forceVideo=1`,
                  );
                  onRequestClose();
                }}
              >
                {isVGB
                  ? 'Record my own greeting'
                  : 'Replace with custom prompt'}
              </Button>
            )}
          </Fragment>
        )}
        {step === STEPS.SHARE && (
          <Fragment>
            {!headerless && <h4 className="'mb-0 mt-2 mb-1">{shareTitle}</h4>}
            <div className="d-flex">
              <Button
                onClick={setCopied}
                btnStyle="reset"
                className="shutterbug-share-action-button mr-1"
              >
                <div>
                  <LinkIcon color="#0d84fe" size={20} />
                </div>
                {copied ? 'Copied!' : 'Copy'}
              </Button>
              {isOwner &&
                !isSpecialtyAlbum &&
                QRCodeMarketingMaterial &&
                basename !== BASENAME.SHUTTERBUG &&
                featurePermissionItem.qr_code_enabled.enabled && (
                  <Button
                    onClick={() => setStep(STEPS.QR)}
                    btnStyle="reset"
                    className="shutterbug-share-action-button"
                  >
                    <div>
                      <QRIcon color="#0d84fe" size={20} />
                    </div>
                    QR
                  </Button>
                )}
              {user && (
                <>
                  <Button
                    onClick={() => setSubStep(SUB_STEPS.SMS)}
                    btnStyle="reset"
                    className="shutterbug-share-action-button mr-1"
                  >
                    <div>
                      <HelpBubblesIcon color="#0d84fe" size={20} />
                    </div>
                    SMS
                  </Button>
                  <Button
                    onClick={() => setSubStep(SUB_STEPS.EMAIL)}
                    btnStyle="reset"
                    className="shutterbug-share-action-button mr-1"
                  >
                    <div>
                      <EmailIcon color="#0d84fe" size={20} />
                    </div>
                    Email
                  </Button>
                </>
              )}
            </div>
            {subStep === SUB_STEPS.SMS && (
              <PhoneStep
                sendInvites={onSendInvites}
                onSuccess={() => setSuccess(true)}
                submitting={loading}
              />
            )}

            {subStep === SUB_STEPS.EMAIL && (
              <EmailStep
                sendInvites={onSendInvites}
                onSuccess={() => setSuccess(true)}
                submitting={loading}
              />
            )}
            {album?.slideshow?.status === ALBUM_SLIDESHOW_STATUS.PUBLISHED && (
              <Fragment>
                <h4 className="mb-0 mt-2">Embed in webpage</h4>
                <p className="text-light text-sm">
                  Paste your slideshow&apos;s HTML embed code into your webpage.
                </p>
                <pre className="slideshow-share-embed-code-container">
                  {album.slideshow.embed_code}
                </pre>
                <Button onClick={setCopiedEmbed} className="copy-embed-cta">
                  {copiedEmbed ? 'Copied!' : `</> Copy HTML`}
                </Button>
              </Fragment>
            )}
          </Fragment>
        )}
        {step === STEPS.MARKETING && (
          <MarketingMaterialsShare
            headless
            albumId={album?.id}
            albumFolderId={albumFolder?.id ?? album?.album_folder?.id}
          />
        )}
        {step === STEPS.QR && (
          <Fragment>
            <Button
              onClick={() => setStep(STEPS.SHARE)}
              btnStyle="link"
              className="mb-2 text-bold"
            >
              <Angle direction="left" className="mr-1" /> Back
            </Button>
            <div className="d-flex flex-column align-items-center">
              <h3 className="mb-4">Share QR Code</h3>
              <img
                src={appendAlbumAsSource(QRCodeMarketingMaterial.image_url)}
                alt="QR Code"
                style={{ width: '250px', height: 'auto' }}
              />

              <AssetDownloadButton
                btnStyle="primary"
                accountMarketingMaterialId={QRCodeMarketingMaterial?.id}
                assetKey="image_download_url"
                label="Download PNG"
                className="mt-2"
              />

              {!featurePermissionItem.white_label_marketing_prints_enabled
                .enabled &&
                featurePermissionItem.type ===
                  FEATURE_PERMISSION_TYPE.PURCHASED_EVENT_FEATURES && (
                  <WeddingEventFeatureUpsell
                    albumFolderId={
                      albumFolder?.id ?? album?.root_album_folder?.id
                    }
                    featureId={
                      PURCHASED_EVENT_FEATURES.white_label_marketing_prints_enabled
                    }
                    banner
                    className="mt-2"
                  />
                )}

              {!featurePermissionItem.white_label_marketing_prints_enabled
                .enabled &&
                featurePermissionItem.type ===
                  FEATURE_PERMISSION_TYPE.SEARCH_SUBSCRIPTION_FEATURE && (
                  <SearchSubscriptionFeatureUpsell
                    featureId={
                      SEARCH_SUBSCRIPTION_FEATURES.white_label_marketing_prints_enabled
                    }
                    banner
                    className="mt-2"
                  />
                )}

              {!featurePermissionItem.white_label_marketing_prints_enabled
                .enabled &&
                featurePermissionItem.type ===
                  FEATURE_PERMISSION_TYPE.PURCHASED_EVENT_ADDON_FEATURES && (
                  <WaldoficationFeatureUpsellBanner
                    featureId={
                      PURCHASED_EVENT_ADDON_FEATURES.white_label_marketing_prints_enabled
                    }
                    albumSubType={
                      (albumFolder ?? album?.root_album_folder)?.album_subtype
                    }
                    albumFolderId={
                      albumFolder?.id ?? album?.root_album_folder?.id
                    }
                    className="mt-2"
                    albumId={album?.id}
                    withModal={false}
                  />
                )}
            </div>
          </Fragment>
        )}
        {withSaveTheDateSetup &&
          step === STEPS.SHARE &&
          entity?.album_subtype === ALBUM_SUBTYPE.WEDDINGS && (
            <SaveTheDateSetup
              albumFolder={albumFolder ?? album?.album_folder}
            />
          )}
      </div>

      {user && (
        <AccountSetupModal
          user={user}
          forceOpen={accountSetupFormOpen}
          onAccountSetupComplete={() => {
            setAccountSetupFormOpen(false);
          }}
          onCancelAccountSetup={() => {
            onRequestClose?.();
            setAccountSetupFormOpen(false);
          }}
        />
      )}
    </Fragment>
  );
};

const withAlbumForContributorShare = graphql(AlbumForContributorShare, {
  options: ({ match }) => ({
    variables: {
      id: match.params.albumId,
    },
  }),
  props: ({ data: { album, loading } }) => ({
    album,
    albumFolder: album?.album_folder,
    loading,
  }),
  skip: ({ match }) => !match.params.albumId,
});

const withAlbumFolderForContributorShare = graphql(
  AlbumFolderForContributorShare,
  {
    options: ({ match }) => ({
      variables: {
        id: match.params.folderId,
      },
    }),
    props: ({ data: { albumFolder, loading } }) => ({
      albumFolder,
      loading,
    }),
    skip: ({ match }) => !match.params.folderId,
  },
);

const withContributorAssignmentShareCreateMutation = graphql(
  autoContributorInvitationsSendMutation,
  {
    props: ({ mutate }) => ({
      autoContributorInvitationsSend: ({
        album_id,
        album_folder_id,
        email_addresses,
        circle_id,
        phone_numbers,
      }) =>
        mutate({
          variables: {
            input: {
              email_addresses,
              album_id,
              album_folder_id,
              circle_id,
              phone_numbers,
            },
          },
        }),
    }),
  },
);

const withAlbumWithGreetingVideo = graphql(AlbumWithGreetingVideo, {
  options: ({ album, match }) => ({
    variables: {
      id: album?.id ?? match.params.albumId,
    },
    fetchPolicy: 'cache-and-network',
  }),
  props: ({ data: { album } }) => ({
    greetingVideo:
      album?.greeting_video?.records?.[0] ??
      album?.slideshow?.default_greeting_video,
    isDefaultGreetingVideo:
      !album?.greeting_video?.records?.[0] &&
      !!album?.slideshow?.default_greeting_video,
  }),
});

const withVideoDelete = graphql(VideoDeleteMutation, {
  props: ({ mutate, ownProps }) => ({
    videoDelete: ({ video_id: videoId }) =>
      mutate({
        variables: {
          input: {
            video_id: videoId,
          },
        },
        refetchQueries: [
          {
            query: AlbumWithGreetingVideo,
            variables: {
              id: ownProps.album?.id ?? ownProps.match?.params?.albumId,
            },
          },
        ],
      }),
  }),
});

const mapDispatchToProps = {
  requestConfirm: requestConfirmAction,
};

ShutterbugShare.propTypes = propTypes;

export { ShutterbugShare as ShutterbugShareComp };

export default compose(
  withRouter,
  withAlbumForContributorShare,
  withAlbumFolderForContributorShare,
  withContributorAssignmentShareCreateMutation,
  withAlbumWithGreetingVideo,
  withVideoDelete,
  connect(null, mapDispatchToProps),
)(ShutterbugShare);
